/* eslint-disable no-useless-escape */
/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  FormInput,
  FormUpload,
  PrimaryButton,
  RedButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm, SubmitHandler } from "react-hook-form";
import { useArticles } from "../../store/useArticles";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUploadImage } from "../../store/useUploadImage";
import { useAlert } from "../../store/useAlert";
import { useMarketPricing } from "../../store/useMarketPricing";
import YouTube from "react-youtube";
import JoditEditor from "jodit-react";
import "./richtext.css";
import { useDropLocations } from "../../store/useDropLocations";
import {
  DropLocationInputItem,
  DynamicFormInput,
} from "../../components/form/DynamicFormInput";

type Inputs = {
  en_title: string;
  nl_title: string;
  category: string;
  homeSection: string;
  description: string;
  linkedArticle?: string;
  videoId?: string;
  externalLink?: string;
};

const HOME_SECTION_OPTIONS = [
  { _id: "NEWS", title: "News" },
  { _id: "DROPPIE", title: "Droppie" },
];

function EditArticleScreen() {
  /** Refs */
  const editorRef = useRef(null);
  /** Hooks */
  const params = useParams();
  const { setAlert } = useAlert();
  const {
    article,
    getArticle,
    getCategories,
    categories,
    clearUpdatedArticle,
    updateArticle,
    updatedArticle,
    addArticleCategory,
  } = useArticles();
  const { getAllMarketPrices, marketPrices, updateMarketPricingLinkedArticle } =
    useMarketPricing();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  const navigate = useNavigate();
  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    resetField,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      homeSection: "DROPPIE",
    },
  });
  const { dropLocations, getDropLocations } = useDropLocations();
  const selectedHomeSection = watch("homeSection");
  const isNewsSection = selectedHomeSection === "NEWS";
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [enContent, setEnContent] = useState("");
  const [nlContent, setNlContent] = useState("");
  const [modal, setShowModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [cities, setCities] = useState<DropLocationInputItem[]>([]);
  const [locationType, setLocationType] = useState<'cities' | 'locations'>('cities');
  const [locations, setLocations] = useState<DropLocationInputItem[]>([]);

  const options = [
    "bold",
    "italic",
    // "|",
    // "ul",
    // "ol",
    "|",
    "outdent",
    "indent",
    "align",
    "|",
    "hr",
    "|",
    // "table",
    "link",
    "|",
    "undo",
    "redo",
  ];

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: "",
      defaultActionOnPaste: "insert_as_html",
      defaultLineHeight: 1.5,
      enter: "div",
      // options that we defined in above step.
      buttons: options,
      buttonsMD: options,
      buttonsSM: options,
      buttonsXS: options,
      statusbar: false,
      sizeLG: 900,
      sizeMD: 700,
      sizeSM: 400,
      toolbarAdaptive: false,
    }),
    [options]
  );

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    setSubmitted(false);
    if (data?.linkedArticle) {
      updateMarketPricingLinkedArticle(
        data.linkedArticle,
        params?.id as string
      );
    }
    if (videoError) {
      setAlert({
        isOpen: true,
        message: "Please enter a valid YouTube video URL.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (isNewsSection && !data.externalLink) {
      setAlert({
        isOpen: true,
        message: "For NEWS, external link is required.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    let body: any = {
      ...data,
      homeSection: data.homeSection,
      title: data?.nl_title,
      body: "{UPDATE APP - NOT SUPPORTED ANYMORE}",
      richText: nlContent,
      en_richText: enContent ?? "",
      nl_richText: nlContent ?? "",
      image: uploadedImage,
    };
    if (!isNewsSection && videoId) {
      body.videoId = videoId;
    }
    if (isNewsSection) {
      body.externalLink = data.externalLink;
      delete body.videoId;
      delete body.linkedArticle;
    }
    body.locationType = locationType === 'cities' ? 'CITY' : 'LOCATION';
    if (locationType === 'cities') {
      body.cities = cities?.map((input) => input?.id) ?? [];
      body.locationIds = [];
    } else {
      body.locationIds = locations?.map((input) => input?.id) ?? [];
      body.cities = [];
    }
    updateArticle(params?.id as string, body);
  };

  const goBack = () => navigate(-1);

  useEffect(() => {
    getDropLocations();
    getArticle(params?.id as string);
    getAllMarketPrices();
    getCategories();
    return () => {
      setCities([]);
      setLocations([]);
      clearUploadedImage();
      clearUpdatedArticle();
    };
  }, []);

  useEffect(() => {
    if (article) {
      reset({
        category: article.category,
        homeSection: article.homeSection ?? "DROPPIE",
        description: article.body,
        videoId: article?.videoId ?? "",
        externalLink: (article as any)?.externalLink ?? "",
      });
      setCities(
        article?.cities?.map((ct) => ({
          id: ct,
          value: ct === "info" ? "Informational (users without location)" : ct,
        })) ?? []
      );
      if (article?.locationIds?.length) {
        setLocations(article?.locationIds?.map((lt) => ({ id: lt, value: lt })));
        setLocationType('locations');
      }
      if (article?.locationType) {
        setLocationType(article.locationType === 'CITY' ? 'cities' : 'locations');
      }
      if (!article?.en_title) {
        setValue("en_title", article.title ?? "");
      } else {
        setValue("en_title", article.en_title ?? "");
      }
      if (!article?.nl_title) {
        setValue("nl_title", article.title ?? "");
      } else {
        setValue("nl_title", article.nl_title ?? "");
      }
      if (!article?.nl_richText) {
        setNlContent(article?.richText ?? "");
      } else {
        setNlContent(article.nl_richText ?? "");
      }
      if (!article?.en_richText) {
        setEnContent(article?.richText ?? "");
      } else {
        setEnContent(article.en_richText ?? "");
      }
      setUploadedImage(article.image as string);
    }
  }, [article]);

  useEffect(() => {
    if (!marketPrices?.length) return;
    const findRelatedArticle =
      marketPrices.find((mPrice) => mPrice.linkedArticle === params?.id) ??
      null;
    if (findRelatedArticle) {
      resetField("linkedArticle", { defaultValue: findRelatedArticle?._id });
    }
  }, [marketPrices, params?.id]);

  useEffect(() => {
    if (article?.locationIds?.length && dropLocations?.length) {
      let newLocationIds: DropLocationInputItem[] = [];
      for (let locationId of article?.locationIds) {
        let location = dropLocations.find((loc) => loc._id === locationId) ?? false;
        if (location) {
          newLocationIds.push({
            id: location._id as string,
            value: location.cost_place as string,
          });
        }
      }
      setLocations(newLocationIds);
    }
  }, [article?.locationIds, dropLocations]);

  useEffect(() => {
    if (updatedArticle) {
      navigate(-1);
    }
  }, [updatedArticle]);

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const imageError = submitted && !image;

  const mappedMarketPrices = marketPrices?.map((mPrice) => ({
    title: mPrice?.name ?? "",
    _id: mPrice?._id ?? "",
  }));

  const getYouTubeVideoId = (url: string) => {
    const regex =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : url;
  };

  useEffect(() => {
    if (isNewsSection) {
      setVideoId(null);
      setVideoError(false);
      return;
    }
    if (watch("videoId")) {
      const videoId = getYouTubeVideoId(watch("videoId") ?? "");
      if (videoId === "ERROR_YOUTUBE") {
        setVideoError(true);
      } else {
        setVideoId(videoId);
        setUploadedImage(
          `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
        );
        setVideoError(false);
      }
    } else {
      setVideoId(null);
      setVideoError(false);
    }
  }, [watch("videoId"), isNewsSection]);

  const addNewCategory = () => {
    if (!categoryName || categoryName === "") {
      setSubmitted(true);
      return;
    }
    setSubmitted(false);
    addArticleCategory(categoryName);
    setCategoryName("");
    setShowModal(false);
  };

  const citiesToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    let locationsToSelect: { value: string; label: string }[] = [
      { label: "Informational (users without location)", value: "info" },
    ];
    for (let location of dropLocations) {
      if (locationsToSelect?.some((loc) => loc?.value === location?.city))
        continue;
      locationsToSelect.push({
        label: location?.city as string,
        value: location?.city as string,
      });
    }
    return locationsToSelect;
  }, [dropLocations]);

  const locationsToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    return dropLocations.map((location) => ({
      label: location?.cost_place as string,
      value: location?._id as string,
    }));
  }, [dropLocations]);

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new article
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full sm:w-1/2 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-0"
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New article
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.en_title)}
              label="Title (English version)"
              placeholder="Article title"
              name="en_title"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.nl_title)}
              label="Title (Dutch version)"
              placeholder="Article title"
              name="nl_title"
              rules={{ required: true }}
            />
            <div className="flex flex-row justify-between items-center">
              <ControlledSelect
                name="category"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.category)}
                label="Category"
                options={categories}
                placeholder="Pick category here"
                inputClassName="w-full"
                className="w-full"
              />

              <button
                type="button"
                onClick={() => setShowModal(true)}
                className={`w-[100px] h-[46px] px-4 rounded font-satoshi text-sm font-medium bg-blue-500 text-white hover:bg-blue-700 ml-2 mt-4`}
              >
                Add
              </button>
            </div>
            <ControlledSelect
              name="homeSection"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.homeSection)}
              label="Home section (News / Droppie on app)"
              options={HOME_SECTION_OPTIONS}
              placeholder="Pick section"
            />
            {isNewsSection ? (
              <>
                <ControlledInput
                  control={control}
                  error={Boolean(errors?.externalLink)}
                  label="External link"
                  placeholder="https://example.com/news-article"
                  name="externalLink"
                  rules={{ required: true }}
                />
              </>
            ) : (
              <>
                <ControlledSelect
                  name="linkedArticle"
                  control={control}
                  rules={{ required: false }}
                  error={Boolean(errors?.linkedArticle)}
                  label="Linked article"
                  options={mappedMarketPrices ?? []}
                  placeholder="Pick article here"
                />
                <ControlledInput
                  control={control}
                  error={videoError}
                  label="Article video"
                  placeholder="Article video"
                  name="videoId"
                  rules={{ required: false }}
                />
              </>
            )}
            {!isNewsSection && videoId && !videoError && (
              <>
                <div className="border border-gray-400 p-[10px] rounded">
                  <p className="mb-2 font-satoshi font-bold text-sm">
                    Video preview
                  </p>
                  <YouTubeVideo videoId={videoId} />
                </div>
                <div className="h-[30px]" />
              </>
            )}
            {!isNewsSection ? (
              <>
                <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
                  Description (English version)
                </p>
                <div className="mb-3">
                  <JoditEditor
                    ref={editorRef}
                    value={enContent}
                    className="h-[500px]"
                    // @ts-ignore
                    config={config}
                    onBlur={(newContent) => setEnContent(newContent)}
                    onChange={(newContent) => { }}
                  />
                </div>
                <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
                  Description (Dutch version)
                </p>
                <div className="mb-3">
                  <JoditEditor
                    ref={editorRef}
                    value={nlContent}
                    className="h-[500px]"
                    // @ts-ignore
                    config={config}
                    onBlur={(newContent) => setNlContent(newContent)}
                    onChange={(newContent) => { }}
                  />
                </div>
              </>
            ) : null}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location Type
              </label>
              <select
                value={locationType}
                onChange={(e) => {
                  setLocationType(e.target.value as 'cities' | 'locations');
                  setCities([]);
                  setLocations([]);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cities">Cities</option>
                <option value="locations">Locations</option>
              </select>
            </div>
            {locationType === 'cities' ? (
              <DynamicFormInput
                generateIds={false}
                label="Cities"
                inputs={cities}
                setInputs={setCities}
                options={citiesToSelect ?? []}
              />
            ) : (
              <DynamicFormInput
                generateIds={false}
                label="Locations"
                inputs={locations}
                setInputs={setLocations}
                options={locationsToSelect ?? []}
              />
            )}
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </div>
        </form>
        <div
          className={`w-full sm:w-1/3 sm:ml-8 bg-white rounded-xl border-gray-border drop-shadow-none h-[360px]`}
        >
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              Article photo
            </p>
          </div>
          <div className="w-full px-7 py-7">
            <FormUpload
              onChange={onImageChange}
              value={image}
              error={imageError}
              imageUrl={uploadedImage as string}
              
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton onClick={goBack} label="Cancel" />
              <PrimaryButton
                onClick={onUploadImage}
                className="ml-4"
                label="Save"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Add Category modal */}
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Add new Courier
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <FormInput
                    label="Name"
                    placeholder="Courier name"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                  />
                  {categoryName === "" && submitted ? (
                    <p className="font-satoshi text-red-600 font-medium">
                      Name is required*
                    </p>
                  ) : null}
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => {
                      setCategoryName("");
                      setSubmitted(false);
                      setShowModal(false);
                    }}
                  />
                  <PrimaryButton
                    disabled={!categoryName || categoryName === ""}
                    label="Add"
                    onClick={addNewCategory}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(EditArticleScreen);

const YouTubeVideo = ({ videoId }: { videoId: string }) => {
  const opts = {
    height: "390",
    width: "100%",
    playerVars: {
      autoplay: 0,
    },
  };

  return <YouTube videoId={videoId} opts={opts} />;
};
